<template>
<section :class="{'has-pagination': pagination}" class="z-table-1">
  <section
    :style="showFixed ? '' : 'content-visibility:hidden;'"
    :class="{
      'has-children': hasChildren
    }"
    class="z-table-wrapper-1"
  >
    <section
      ref="tableBodyWrapper"
      class="z-table-body-wrapper-1"
      @scroll="onTableBodyWrapperScroll"
    >
      <table ref="mainTable" :style="width ? `width:${width}` : ''">
        <colgroup>
          <col
            v-for="(col, colIndex) in allLeafCols"
            :key="colIndex"
            :width="col.width"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in tHRows"
          :key="tRowIndex"
          ref="headRows"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            ref="thCells"
            :prop="col.prop"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >{{col.label}}</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, rowIndex) in filterData"
          :key="rowIndex"
          ref="bodyRows"
          :class="{
            'hover-row': row === hoverWrapper.row,
            'selected-row': row === selectedWrapper.row,
            'expand': showChildrenMap[row[rowKey]]
          }"
          @mouseover="onRowHover(row)"
          @mouseout="onRowMouseout(row)"
          @click="onRowClick(row, rowIndex)"
          @dblclick="onRowDblclick(row, rowIndex)"
        >
          <td
            v-for="(col, colIndex) in allLeafCols"
            :key="colIndex"
            :align="col.align"
            :style="colIndex === 0 && rowDeepMap[row[rowKey]] ? `padding-left:${10 + (rowDeepMap[row[rowKey]]) * 16}px` : ''"
          >
            <span
              v-if="colIndex === 0 && Array.isArray(row.children) && row.children.length > 0"
              class="expand-btn"
              @click.stop="onExpandBtnClick(row)"
            ></span>
            <slot
              v-if="$scopedSlots[col.prop]"
              :name="col.prop"
              :row="row"
              :rowIndex="rowIndex"
            />
            <template v-else>{{row[col.prop]}}</template>
          </td>
        </tr>
        </tbody>
      </table>
    </section>

    <section
      ref="tableHeaderWrapper"
      v-show="showFixed"
      :style="{
        width: `${wrapperWidth}px`
      }"
      class="z-table-header-wrapper-1"
      @mousewheel.prevent="onTableHeaderWrapperMouseWheel"
    >
      <table :width="tableWidth">
        <colgroup>
          <col
            v-for="(col, colIndex) in allLeafCols"
            :key="colIndex"
            :width="colWidthMap[col.prop]"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in tHRows"
          :key="tRowIndex"
          :style="{
            height: `${headRowHeights[tRowIndex]}`
          }"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >{{col.label}}</th>
        </tr>
        </thead>
      </table>
    </section>

    <section
      v-if="hasFixedCols"
      ref="fixedTableBodyWrapper"
      v-show="showFixed"
      :style="{
        height: `${wrapperHeight}px`
      }"
      class="z-table-fixed-body-wrapper-1"
      @mousewheel.prevent="onFixedTableBodyWrapperMouseWheel"
    >
      <table :width="fixedTableWidth">
        <colgroup>
          <col
            v-for="(col, colIndex) in fixedLeafCols"
            :key="colIndex"
            :width="colWidthMap[col.prop]"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in fixedTHRows"
          :key="tRowIndex"
          :style="{
            height: `${headRowHeights[tRowIndex]}`
          }"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >{{col.label}}</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, rowIndex) in filterData"
          :key="rowIndex"
          :style="{
            height: `${bodyRowHeights[rowIndex]}`
          }"
          :class="{
            'hover-row': row === hoverWrapper.row,
            'selected-row': row === selectedWrapper.row,
            'expand': showChildrenMap[row[rowKey]]
          }"
          @mouseover="onRowHover(row)"
          @mouseout="onRowMouseout(row)"
          @click="onRowClick(row, rowIndex)"
          @dblclick="onRowDblclick(row, rowIndex)"
        >
          <td
            v-for="(col, colIndex) in fixedLeafCols"
            :key="colIndex"
            :align="col.align"
            :style="colIndex === 0 && rowDeepMap[row[rowKey]] ? `padding-left:${10 + (rowDeepMap[row[rowKey]]) * 16}px` : ''"
          >
            <span
              v-if="colIndex === 0 && Array.isArray(row.children) && row.children.length > 0"
              class="expand-btn"
              @click.stop="onExpandBtnClick(row)"
            ></span>
            <slot
              v-if="$scopedSlots[col.prop]"
              :name="col.prop"
              :row="row"
              :rowIndex="rowIndex"
            />
            <template v-else>{{row[col.prop]}}</template>
          </td>
        </tr>
        </tbody>
      </table>
    </section>

    <section
      v-if="hasFixedCols"
      v-show="showFixed"
      class="z-table-fixed-header-wrapper-1"
      @mousewheel.prevent="onFixedTableHeaderMouseWheel"
    >
      <table :width="fixedTableWidth">
        <colgroup>
          <col
            v-for="(col, colIndex) in fixedLeafCols"
            :key="colIndex"
            :width="colWidthMap[col.prop]"
          />
        </colgroup>
        <thead>
        <tr
          v-for="(thRow, tRowIndex) in fixedTHRows"
          :key="tRowIndex"
          :style="{
            height: `${headRowHeights[tRowIndex]}`
          }"
        >
          <th
            v-for="(col, tColIndex) in thRow"
            :key="tColIndex"
            :rowspan="col.columns ? 1 : (tHRowNum - tRowIndex)"
            :colspan="col.leafColumns.length"
            :align="col.align"
          >{{col.label}}</th>
        </tr>
        </thead>
      </table>
    </section>
  </section>

  <section v-if="pagination" class="z-pagination-wrapper-1">
    <slot name="z-table-pagination-1" />
    <section class="z-pagination-content-1">
      <span style="color:#606266;">共<span style="color:#409EFF"> {{data.length}} </span>条</span>
      <ul class="z-page-no-list-1">
        <li
          :disabled="paginationConfig.no === 1"
          class="z-page-no-1"
          @click="onSwitchPageBtnClick(-1)"
        >&lt;</li>
        <li
          v-for="pageNo in paginationConfig.pageNos"
          :key="pageNo"
          :class="{selected: paginationConfig.no === pageNo}"
          class="z-page-no-1"
          @click="onPageNoBtnClick(pageNo)"
        >{{pageNo}}</li>
        <li
          :disabled="paginationConfig.no >= paginationConfig.totalPage"
          class="z-page-no-1"
          @click="onSwitchPageBtnClick(1)"
        >&gt;</li>
      </ul>
    </section>
  </section>
</section>
</template>

<script>
import Pagination from './ZTablePagination'
import Column from './ZTableColumn'

export default {
  name: 'ZTable',
  props: {
    columns: {
      required: true,
      type: Array
    },
    data: {
      required: true,
      type: Array
    },
    width: {
      type: String
    },
    rowKey: {},
    selectedRowStyle: {
      type: Boolean,
      default: false
    },
    section: {
      type: Object
    },
    pagination: {
      type: Pagination
    }
  },
  data () {
    return {
      showFixed: true,
      wrapperWidth: 0,
      wrapperHeight: 0,
      tableWidth: 0,
      colWidthMap: Object.freeze({}),
      headRowHeights: Object.freeze([]),
      bodyRowHeights: Object.freeze([]),
      rowDeepMap: {},
      parentKeyMap: {},
      showChildrenMap: {},
      hoverWrapper: Object.freeze({}),
      selectedWrapper: Object.freeze({})
    }
  },
  computed: {
    tCols () {
      return this.columns.map(column => new Column(column))
    },
    fixedTCols () {
      return this.tCols.filter(col => col.fixed)
    },
    hasChildren () {
      return Object.values(this.rowDeepMap).length > 0
    },
    tHRowNum () {
      return Math.max(...this.tCols.map(col => col.rDeep))
    },
    tHRows () {
      return this.getTHRows(this.tCols)
    },
    fixedTHRows () {
      return this.getTHRows(this.fixedTCols)
    },
    allLeafCols () {
      return this.getLeafCols(this.tCols)
    },
    fixedLeafCols () {
      return this.getLeafCols(this.fixedTCols)
    },
    fixedTableWidth () {
      let {colWidthMap} = this
      return this.fixedLeafCols.reduce((sum, col) => sum + colWidthMap[col.prop], 0)
    },
    hasFixedCols () {
      return this.fixedTCols.length > 0
    },
    paginationData () {
      let {data} = this

      if (!Array.isArray(data)) {
        return []
      }

      let {pagination} = this

      if (pagination) {
        let {size} = pagination
        let index = ((pagination.no - 1) * size)
        data = data.slice(index, index + size)
      }

      return data
    },
    filterData () {
      let {rowKey, paginationData} = this

      if (rowKey) {
        let {showChildrenMap} = this
        paginationData = [...paginationData]

        for (let i = 0; i < paginationData.length; i++) {
          let row = paginationData[i]

          if (showChildrenMap[row[rowKey]]) {
            paginationData.splice(i + 1, 0, ...row.children)
          }
        }
      }

      return paginationData
    },
    paginationConfig () {
      let {pagination: {no, size}} = this
      let totalPage = Math.ceil(this.data.length / size)
      let pageNos = []
      let start = Math.max(no - 4, 1)
      let end = Math.min(start + 9, totalPage)

      start = Math.max(end - 9, 1)

      for (let i = start; i <= end; i++) {
        pageNos.push(i)
      }

      return {
        no,
        size,
        totalPage,
        pageNos
      }
    }
  },
  methods: {
    setCurrentRow (row) {
      this.selectedWrapper = Object.freeze({
        row
      })
    },
    getTHRows (tCols) {
      let {tHRowNum} = this
      let tHRows = [tCols]

      for (let i = 1; i < tHRowNum; i++) {
        tHRows.push(tCols = tCols.reduce((row, {columns}) => {
          if (columns) {
            row.push(...columns)
          }

          return row
        }, []))
      }

      return tHRows
    },
    getLeafCols (tCols) {
      return tCols.reduce((colProps, tCol) => {
        colProps.push(...tCol.leafColumns)
        return colProps
      }, [])
    },
    setTreeRowKeyMap () {
      let {rowKey} = this
      let rowDeepMap = {}
      let parentKeyMap = {}
      let showChildrenMap = {}

      if (rowKey) {
        let {data} = this

        if (Array.isArray(data)) {
          let dataList = [...data]

          for (let i = 0; i < dataList.length; i++) {
            let data = dataList[i]
            let {children} = data
            let rowKeyVal = data[rowKey]
            let parentRowKeyDeep = rowDeepMap[parentKeyMap[rowKeyVal]]

            if (Array.isArray(children) && children.length > 0) {
              rowDeepMap[rowKeyVal] = parentRowKeyDeep ? (parentRowKeyDeep + 1) : 1
              showChildrenMap[rowKeyVal] = 0

              dataList.splice(i + 1, 0, ...children)

              children.forEach(data => {
                parentKeyMap[data[rowKey]] = rowKeyVal
              })
            } else if (parentRowKeyDeep) {
              rowDeepMap[rowKeyVal] = parentRowKeyDeep + 1
            }
          }
        }
      }

      this.parentKeyMap = Object.freeze(parentKeyMap)
      this.rowDeepMap = Object.freeze(rowDeepMap)
      this.showChildrenMap = showChildrenMap
    },
    doLayout () {
      let {$refs} = this
      let {tableBodyWrapper, mainTable, headRows, bodyRows} = $refs

      this.wrapperWidth = tableBodyWrapper.clientWidth
      this.wrapperHeight = tableBodyWrapper.clientHeight
      this.tableWidth = window.getComputedStyle(mainTable).width
      if ($refs.thCells) {
        this.colWidthMap = Object.freeze($refs.thCells.reduce((map, cell) => {
          map[cell.getAttribute('prop')] = Number(window.getComputedStyle(cell).width.replace('px', ''))
          return map
        }, {}))
      }
      this.headRowHeights = Object.freeze(headRows.map(row => window.getComputedStyle(row).height))

      if (bodyRows) {
        this.bodyRowHeights = Object.freeze(bodyRows.map(row => window.getComputedStyle(row).height))
      }
    },
    clearDoLayoutTimer () {
      clearTimeout(this.doLayoutTimer)
    },
    readyDoLayout (delay) {
      console.time('ZTable')
      this.clearDoLayoutTimer()

      this.doLayoutTimer = setTimeout(() => {
        this.showFixed = false
        this.$nextTick(() => {
          this.doLayout()

          this.$nextTick(() => {
            this.showFixed = true
            console.timeEnd('ZTable')
          })
        })
      }, delay)
    },
    scrollToTop () {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      tableBodyWrapper.scrollTop = tableBodyWrapper.scrollLeft = $refs.tableHeaderWrapper.scrollLeft = 0

      if (this.hasFixedCols) {
        $refs.fixedTableBodyWrapper.scrollTop = 0
      }
    },
    onWindowResize () {
      this.readyDoLayout(100)
    },
    clearScrollStatusTimer () {
      clearTimeout(this.scrollStatusTimer)
    },
    setScrollStatus () {
      this.clearScrollStatusTimer()

      this.scrolling = true

      this.scrollStatusTimer = setTimeout(() => {
        this.scrolling = false
      }, 300)
    },
    onTableBodyWrapperScroll (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs

      $refs.tableHeaderWrapper.scrollLeft = tableBodyWrapper.scrollLeft

      if (this.fixedTCols.length > 0) {
        $refs.fixedTableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop
      }

      this.setScrollStatus()
    },
    onFixedTableBodyWrapperMouseWheel (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      $refs.fixedTableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop + event.deltaY
      this.setScrollStatus()
    },
    onTableHeaderWrapperMouseWheel (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      $refs.tableHeaderWrapper.scrollLeft = tableBodyWrapper.scrollLeft = tableBodyWrapper.scrollLeft + event.deltaX
      this.setScrollStatus()
    },
    onFixedTableHeaderMouseWheel (event) {
      let {$refs} = this
      let {tableBodyWrapper} = $refs
      $refs.fixedTableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop = tableBodyWrapper.scrollTop + event.deltaY
      $refs.tableHeaderWrapper.scrollLeft = tableBodyWrapper.scrollLeft = tableBodyWrapper.scrollLeft + event.deltaX
      this.setScrollStatus()
    },
    onRowHover (row) {
      if (this.scrolling) {
        return
      }

      this.hoverWrapper = Object.freeze({
        row
      })
      this.$emit('row-hover', row)
    },
    onRowMouseout (row) {
      if (this.scrolling) {
        return
      }

      this.hoverWrapper = Object.freeze({})
      this.$emit('row-mouseout', row)
    },
    setRowSelectStyle (row) {
      if (this.selectedRowStyle) {
        if (this.selectedWrapper.row !== row) {
          this.selectedWrapper = Object.freeze({
            row
          })

          return true
        } else {
          this.selectedWrapper = Object.freeze({})
        }
      }

      return false
    },
    onRowClick (row) {
      this.$emit('row-click', row, this.setRowSelectStyle(row))
    },
    onRowDblclick (row) {
      this.setRowSelectStyle(row)
      this.$emit('row-dblclick', row)
    },
    onExpandBtnClick (row) {
      let rowKeyVal = row[this.rowKey]
      let {showChildrenMap} = this
      showChildrenMap[rowKeyVal] = !showChildrenMap[rowKeyVal]
      this.readyDoLayout(0)
    },
    onPageNoBtnClick (pageNo) {
      let {pagination} = this

      if (pagination.no === pageNo) {
        return
      }

      pagination.no = pageNo

      this.scrollToTop()

      this.$emit('pagination-no-changed', pageNo)
      this.readyDoLayout(0)
    },
    onSwitchPageBtnClick (direction) {
      let pageNo = this.pagination.no + direction

      if (pageNo < 1 || pageNo > this.paginationConfig.totalPage) {
        return
      }

      this.onPageNoBtnClick(pageNo)
    }
  },
  watch: {
    tCols: {
      immediate: true,
      handler (newVal) {
        this.readyDoLayout(0)
      }
    },
    data: {
      immediate: true,
      handler (newVal) {
        this.setTreeRowKeyMap()
      }
    },
    filterData: {
      immediate: true,
      handler (newVal) {
        this.readyDoLayout(0)
      }
    }
  },
  created () {
    window.addEventListener('resize', this.onWindowResize, true)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onWindowResize, true)
    this.clearDoLayoutTimer()
    this.clearScrollStatusTimer()
  }
}
</script>

<style lang="scss">
.z-table-1 {
  $PAGINATION-HEIGHT: 40px;

  position: relative;
  width: 100%;
  height: 100%;
  white-space: var(--z-table-white-space);

  &.has-pagination {
    padding-bottom: $PAGINATION-HEIGHT;
  }

  table {
    table-layout: var(--z-table-layout, fixed);
    border-collapse: collapse;
    border-spacing: 0;
    border: 0;
  }

  .z-pagination-wrapper-1 {
    position: absolute;
    font-size: 14px;
    bottom: 0;
    left: 0;
    width: 100%;
    height: $PAGINATION-HEIGHT;
    padding-top: 10px;
  }
}

.z-table-wrapper-1 {
  --z-table-current-border-color: var(--z-table-border-color, var(--z-common-border-color, #EBEEF5));

  position: relative;
  font-size: .875rem;
  width: 100%;
  height: 100%;
  border: 1px solid var(--z-table-current-border-color);
  /*content-visibility: hidden;*/
  content-visibility: auto;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;

  &.has-children {
    td:first-child {
      padding-left: 26px;
    }

    tr {
      &.expand {
        .expand-btn {
          &:before {
            transform: rotate(135deg);
          }
        }
      }
    }

    .expand-btn {
      display: inline-block;
      float: left;
      width: 16px;
      height: 22px;
      margin-left: -26px;
      padding: 8px 0 8px 10px;
      text-align: center;
      cursor: pointer;

      &:hover {
        &:before {
          --z-table-expand-btn-hover-border-color: var(--z-table-expand-btn-hover-color, #409EFF);
          border-color: var(--z-table-expand-btn-hover-border-color);
        }
      }

      &:before {
        --z-table-expand-btn-border-color: var(--z-table-expand-btn-color, #909399);

        content: "";
        display: block;
        width: 100%;
        height: 100%;
        border: {
          top: 1px solid var(--z-table-expand-btn-border-color);
          right: 1px solid var(--z-table-expand-btn-border-color);
        };
        transform: rotate(45deg);
      }
    }
  }

  > .z-table-header-wrapper-1,
  > .z-table-fixed-body-wrapper-1,
  > .z-table-fixed-header-wrapper-1 {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    overflow: hidden;
  }

  > .z-table-header-wrapper-1,
  > .z-table-fixed-header-wrapper-1 {
    border-bottom: 1px solid var(--z-table-current-border-color);
    background-color: var(--z-table-bg-color, #f9f9f9);

    table {
      margin-bottom: -1px;
    }
  }

  > .z-table-header-wrapper-1,
  > .z-table-body-wrapper-1 {
    th,
    td {
      &:last-child {
        border-right: 0;
      }
    }
  }

  > .z-table-body-wrapper-1,
  > .z-table-fixed-body-wrapper-1 {
    table {
      background-color: var(--z-table-body-bg-color, #ffffff);
    }

    tbody {
      tr {
        &:nth-child(2n) {
          background-color: var(--z-table-zebra-crossing-bg-color, #fafafa);
        }

        &.hover-row {
          background-color: var(--z-table-row-hover-bg-color, #f5f7fa);
        }

        &.selected-row {
          background-color: var(--z-table-row-selected-bg-color, #ecf5ff);
        }
      }
    }
  }

  > .z-table-body-wrapper-1 {
    width: 100%;
    height: 100%;
    overflow: auto;

    > table {
      min-width: var(--z-table-width, 100%);
      border-right: 0;
    }
  }

  th,
  td {
    /*min-width: 50px;*/
    padding: 6px 10px;
    line-height: 1.6428571428571428;
    border: 1px solid var(--z-table-current-border-color);
    text-overflow: ellipsis;
    /*word-break: break-all;*/
    overflow: hidden;

    &:first-child {
      border-left: 0;
    }
  }

  table {
    border-color: var(--z-table-current-border-color);
  }

  tr {
    cursor: default;

    &:first-child {
      th {
        border-top: 0;
      }
    }
  }

  th {
    color: var(--z-table-th-color, #303030);
  }

  td {
    color: var(--z-table-td-color, #606266);
  }
}

.z-pagination-wrapper-1 {
  display: grid;
  grid-template-columns: auto max-content;
  grid-column-gap: 30px;
  align-items: center;
  background-color: var(--z-pagination-bg-color, #ffffff);
  user-select: none;

  .z-pagination-content-1 {
    grid-column: 2;
    height: 100%;
    line-height: 30px;
  }

  .z-page-no-list-1 {
    float: right;
    height: 100%;
    margin: 0 0 0 1em;
    padding: 0;
    font-weight: 500;
    list-style: none;

    .z-page-no-1 {
      float: left;
      width: 30px;
      margin: 0 1px;
      text-align: center;
      color: var(--z-pagination-no-color, #606266);
      background-color: var(--z-pagination-no-bg-color, #f4f4f5);
      cursor: pointer;

      &.selected {
        color: var(--z-pagination-no-selected-color, #ffffff);
        background-color: var(--z-pagination-no-selected-bg-color, #409eff);
      }

      &[disabled="disabled"] {
        color: var(--z-pagination-no-disabled-color, #c0c4cc);
        cursor: not-allowed;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/06Data-Management/02Normal/08VOCs-Data-Compare/ZTable.vue